import type { NativeApprovalDescription } from "@skaile/workspaces/types";
import type { CodexServerRequestHandlers } from "../codex-app-server.js";
import type { NativeNotifications, NativeServerRequests } from "./native-protocol.generated.js";
import {
  allowsNativeApprovalPath,
  isNativeApprovalPolicyActive,
  type NativeApprovalPolicy,
} from "./native-policy.js";
import type { CodexQuestions } from "./questions.js";

type ApprovalMethod =
  | "item/commandExecution/requestApproval"
  | "item/fileChange/requestApproval"
  | "item/permissions/requestApproval";
type Identity = { itemId: string; threadId: string; turnId: string };
type Permissions =
  NativeServerRequests["item/permissions/requestApproval"]["params"]["permissions"];

/** Changes from a retained native file-change item in the same thread and turn. */
export type NativeApprovalFileChanges = Extract<
  NativeNotifications["item/started"]["item"],
  { type: "fileChange" }
>["changes"];

/** Driver-owned consent hooks; the existing native callback wrapper owns turn/generation binding. */
export function createCodexNativeApprovalHandlers(options: {
  questions: CodexQuestions;
  getPolicy: () => NativeApprovalPolicy | undefined;
  getFileChanges: (identity: Identity) => NativeApprovalFileChanges | undefined;
}): Pick<CodexServerRequestHandlers, ApprovalMethod> {
  const active = (signal: AbortSignal, threadId: string) =>
    !signal.aborted && isNativeApprovalPolicyActive(options.getPolicy(), threadId);
  const text = (value: unknown): value is string =>
    typeof value === "string" && value.length > 0 && value.length <= 8192;
  const scoped = async (
    path: unknown,
    access: "read" | "write",
    signal: AbortSignal,
    threadId: string,
  ) =>
    active(signal, threadId) &&
    text(path) &&
    allowsNativeApprovalPath(options.getPolicy(), path, access) &&
    active(signal, threadId);
  const permissions = async (value: Permissions, signal: AbortSignal, threadId: string) => {
    if (
      value.network?.enabled ||
      (value.network != null && Object.keys(value.network).some((key) => key !== "enabled")) ||
      Object.keys(value).some((key) => !["network", "fileSystem"].includes(key))
    )
      return false;
    const fs = value.fileSystem;
    if (!fs) return true;
    if (
      fs.entries != null ||
      fs.globScanMaxDepth != null ||
      Object.keys(fs).some((key) => !["read", "write", "entries", "globScanMaxDepth"].includes(key))
    )
      return false;
    for (const access of ["read", "write"] as const) {
      const paths = fs[access];
      if (paths != null && (!Array.isArray(paths) || paths.length > 32)) return false;
      for (const path of paths ?? [])
        if (!(await scoped(path, access, signal, threadId))) return false;
    }
    return active(signal, threadId);
  };
  const consent = async (
    identity: Identity,
    approval: NativeApprovalDescription,
    signal: AbortSignal,
  ) => {
    if (
      !active(signal, identity.threadId) ||
      approval.details.length > 64 ||
      approval.details.some((detail) => detail.length > 8192) ||
      approval.details.reduce((sum, detail) => sum + detail.length, 0) > 65536
    )
      return false;
    try {
      return (
        (await options.questions.requestApproval(identity, approval, signal)) &&
        active(signal, identity.threadId)
      );
    } catch {
      return false;
    }
  };
  return {
    "item/commandExecution/requestApproval": async (input, signal) => {
      const params = structuredClone(input);
      const allowed = async () =>
        active(signal, params.threadId) &&
        text(params.command) &&
        (params.kind == null || params.kind === "command") &&
        !params.environmentId &&
        !params.networkApprovalContext &&
        (params.availableDecisions == null || params.availableDecisions.includes("accept")) &&
        (await scoped(params.cwd, "read", signal, params.threadId)) &&
        (!params.additionalPermissions ||
          (await permissions(params.additionalPermissions, signal, params.threadId)));
      if (!(await allowed())) return { decision: "decline" };
      const details = [`Command: ${params.command}`, `Directory: ${params.cwd}`];
      for (const access of ["read", "write"] as const)
        for (const path of params.additionalPermissions?.fileSystem?.[access] ?? [])
          details.push(`${access}: ${path}`);
      const accepted = await consent(params, { kind: "command", details }, signal);
      return { decision: accepted && (await allowed()) ? "accept" : "decline" };
    },
    "item/fileChange/requestApproval": async (input, signal) => {
      const params = structuredClone(input);
      if (!active(signal, params.threadId)) return { decision: "decline" };
      const original = options.getFileChanges(params);
      if (!original?.length || original.length > 16) return { decision: "decline" };
      const changes = structuredClone(original);
      const snapshot = JSON.stringify(changes);
      const allowed = async () => {
        if (
          !active(signal, params.threadId) ||
          snapshot !== JSON.stringify(options.getFileChanges(params))
        )
          return false;
        if (
          params.grantRoot != null &&
          !(await scoped(params.grantRoot, "write", signal, params.threadId))
        )
          return false;
        for (const change of changes) {
          if (!text(change.diff) || !(await scoped(change.path, "write", signal, params.threadId)))
            return false;
          if (!["add", "delete", "update"].includes(change.kind.type)) return false;
          if (
            change.kind.type === "update" &&
            change.kind.move_path != null &&
            !(await scoped(change.kind.move_path, "write", signal, params.threadId))
          )
            return false;
        }
        return active(signal, params.threadId);
      };
      if (!(await allowed())) return { decision: "decline" };
      const details = changes.flatMap((change) => [
        `${change.kind.type}: ${change.path}${change.kind.type === "update" && change.kind.move_path ? ` → ${change.kind.move_path}` : ""}`,
        change.diff,
      ]);
      const accepted = await consent(params, { kind: "file", details }, signal);
      return { decision: accepted && (await allowed()) ? "accept" : "decline" };
    },
    "item/permissions/requestApproval": async (input, signal) => {
      const params = structuredClone(input);
      const allowed = async () =>
        active(signal, params.threadId) &&
        !params.environmentId &&
        (await scoped(params.cwd, "read", signal, params.threadId)) &&
        (await permissions(params.permissions, signal, params.threadId));
      if (!(await allowed())) return { permissions: {}, scope: "turn" };
      const details = [`Directory: ${params.cwd}`];
      for (const access of ["read", "write"] as const)
        for (const path of params.permissions.fileSystem?.[access] ?? [])
          details.push(`${access}: ${path}`);
      if (details.length === 1) return { permissions: {}, scope: "turn" };
      const accepted = await consent(params, { kind: "permissions", details }, signal);
      return {
        permissions: accepted && (await allowed()) ? params.permissions : {},
        scope: "turn",
      };
    },
  };
}
